import React, { useState } from 'react';
import ServiceRequestForm from 'src/components/ServiceRequestForm/ServiceRequestForm';
import './sr-heading-action-group.scss';
import Heading from '../../components/Heading';
import Row from '../../components/Row';
import Col from '../../components/Col';
import StatusOverviewCard from './components/StatusOverviewCard';
import DaysSinceSubmissionCard from './components/DaysSinceSubmissionCard';
import { ServiceRequestDashboardTable } from './components/ServiceRequestDashboardTable';
import { ServiceRequestDashboardActions } from './components/ServiceRequestDashboardActions';
import ServiceRequestDashboardToolbar from './components/ServiceRequestDashboardToolbar/ServiceRequestDashboardToolbar';
import { FilterOverlay } from './components/Overlays/FilterOverlay';
import { GroupingOverlay } from './components/Overlays/GroupingOverlay';
import { DeclinedStatusOverlay } from './components/Overlays/BulkStatus/DeclinedStatusOverlay';
import { AssignedStatusOverlay } from './components/Overlays/BulkStatus/AssignedStatusOverlay';
import { FilterBar } from './components/FilterBar';
import { DownloadCSVAndPDFParserOverlay } from 'src/utils/DownloadCSVAndPDFParserOverlay/DownloadCSVAndPDFParserOverlay';
import WithNavigation from 'src/layouts/WithNavigation/WithNavigation';
import Button from 'src/components/Button/Button';
import FontAwesomeIcon from 'src/components/Icon';
import StatusManager from 'src/components/StatusManager/StatusManager';
import ServiceRequestVisibilityDialogue from './components/ServiceRequestVisibilityDialogue/ServiceRequestVisibilityDialogue';
import {
  useServiceRequestsPageOrchestrator,
  UseServiceRequestsPageOrchestratorReturn,
} from './CustomHooks/useServiceRequestsPageOrchestrator';
import { useServiceRequestFilters } from './CustomHooks/useServiceRequestFilters';
import { PAGINATION_KEYS } from 'src/store/slices/persistedUiSlice';

export default () => {
  const {
    filters,
    formattedFiltersForApi,
    applyFilter,
    applyFilters,
    removeFilter,
    clearFilters,
  } = useServiceRequestFilters();

  // Todo: Maybe this god orchestrator can be decomposed better int he future but this is still a step up from the old BOS GOD component.
  const {
    serviceRequests,
    visibleServiceRequests,
    statusOverview,
    ageOverview,
    loading,

    searchString,
    setSearchString,

    selectedIds,
    selectedServiceRequests,
    selectedCount,
    allSelected,
    toggleAllSelected,
    handleSelectServiceRequests,

    showResolved,
    handleToggleShowResolved,
    disableShowResolved,

    showSubscribedOnly,
    handleToggleShowSubscribedOnly,

    showColumnSelectionDialog,
    openColumnSelectionDialog,
    closeColumnSelectionDialog,

    canManageStatuses,
    showStatusManager,
    openStatusManager,
    closeStatusManager,

    showVisibilityDialogue,
    openVisibilityDialogue,
    closeVisibilityDialogue,

    showFilterOverlay,
    openFilterOverlay,
    setShowFilterOverlay,

    showGroupingOverlay,
    groupingPropertyId,
    handleOpenGroupingOverlay,
    setShowGroupingOverlay,

    showDeclinedStatusOverlay,
    setShowDeclinedStatusOverlay,

    showAssignedStatusOverlay,
    setShowAssignedStatusOverlay,

    showDownloadOverlay,
    openDownloadOverlay,
    closeDownloadOverlay,

    handleBulkStatusUpdate,
    handleDeleteGroup,
    handleDeleteItemFromGroup,

    fetchData,

    onDownloadCSV,
    onDownloadPDF,
    isCSVLoading,
    isPDFLoading,
  }: UseServiceRequestsPageOrchestratorReturn = useServiceRequestsPageOrchestrator(
    {
      filters,
      formattedFiltersForApi,
      applyFilter,
    }
  );

  const [isServiceRequestFormOpen, setIsServiceRequestFormOpen] =
    useState<boolean>(false);

  return (
    <WithNavigation
      id="service-request-dashboard"
      title="Service Requests"
      testId="service-request-dashboard"
      top={
        <Heading
          headline="Service Requests"
          headlineAction={
            <div className="sr-heading-action-group">
              <Button
                color="teal"
                label="Create Service Request"
                size="small"
                onClick={() => setIsServiceRequestFormOpen(true)}
                icon={
                  <FontAwesomeIcon glyph="wrench" weight="light" size="small" />
                }
                block={undefined}
                disabled={undefined}
                outlined={undefined}
                outline={undefined}
                weight={undefined}
                children={undefined}
                hideLabel={false}
                id="serviceRequestCreateButton"
              />
              {canManageStatuses && (
                <div className="sr-heading-manage">
                  <Button
                    outline="teal"
                    size="small"
                    label="Manage Custom Statuses"
                    icon={
                      <FontAwesomeIcon
                        glyph="pencil"
                        weight="light"
                        size="small"
                      />
                    }
                    onClick={openStatusManager}
                    id="serviceRequestManageStatusesButton"
                  />
                </div>
              )}
            </div>
          }
        >
          <Row chonky>
            <Col xs={12} md={6} chonky>
              <StatusOverviewCard
                loading={loading}
                serviceRequestOverview={statusOverview}
                onSelectStatus={disableShowResolved}
                filters={filters}
                applyFilter={applyFilter}
              />
            </Col>
            <Col xs={12} md={6} chonky>
              <DaysSinceSubmissionCard
                loading={loading}
                serviceRequestOverview={ageOverview}
                filters={filters}
                applyFilter={applyFilter}
              />
            </Col>
          </Row>
        </Heading>
      }
    >
      <div className="tabs">
        <StatusManager
          isOpen={showStatusManager}
          onClose={closeStatusManager}
          onApplied={fetchData}
        />
        <ServiceRequestVisibilityDialogue
          isOpen={showVisibilityDialogue}
          onClose={closeVisibilityDialogue}
          onSuccess={() => {
            void fetchData();
          }}
        />
        <FilterOverlay
          showFilterOverlay={showFilterOverlay}
          setShowFilterOverlay={setShowFilterOverlay}
          filters={filters}
          applyFilters={applyFilters}
        />
        <GroupingOverlay
          showGroupOverlay={showGroupingOverlay}
          setShowGroupOverlay={setShowGroupingOverlay}
          selectedServiceRequestIds={selectedIds}
          groupingPropertyId={groupingPropertyId}
          fetchDashboard={fetchData}
          existingGroups={[]}
        />
        <DeclinedStatusOverlay
          showDeclinedStatusOverlay={showDeclinedStatusOverlay}
          setShowDeclinedStatusOverlay={setShowDeclinedStatusOverlay}
          serviceRequestIds={
            allSelected ? serviceRequests.map((r) => r.id) : selectedIds
          }
          fetchDashboard={fetchData}
        />
        <AssignedStatusOverlay
          showAssignedStatusOverlay={showAssignedStatusOverlay}
          setShowAssignedStatusOverlay={setShowAssignedStatusOverlay}
          serviceRequests={
            allSelected ? serviceRequests : selectedServiceRequests
          }
          fetchDashboard={fetchData}
        />

        <DownloadCSVAndPDFParserOverlay
          isOpen={showDownloadOverlay}
          onClose={closeDownloadOverlay}
          onDownloadCSV={onDownloadCSV}
          isCSVLoading={isCSVLoading}
          onDownloadPDF={onDownloadPDF}
          isPDFLoading={isPDFLoading}
        />
        <ServiceRequestDashboardToolbar>
          <ServiceRequestDashboardActions
            onChangeSearchString={setSearchString}
            filterCount={0}
            showResolved={showResolved}
            onShowResolved={handleToggleShowResolved}
            showSubscribedOnly={showSubscribedOnly}
            onToggleShowSubscribedOnly={handleToggleShowSubscribedOnly}
            onShowFilterOverlay={openFilterOverlay}
            searchString={searchString}
            onBulkStatusUpdate={handleBulkStatusUpdate}
            onShowColumnSelectionDialog={openColumnSelectionDialog}
            onGroup={handleOpenGroupingOverlay}
            disableGroup={!allSelected && selectedIds.length === 0}
            onDownload={openDownloadOverlay}
            selectedCount={selectedCount}
            onManageVisibility={openVisibilityDialogue}
          />
        </ServiceRequestDashboardToolbar>
        {isServiceRequestFormOpen && (
          <ServiceRequestForm
            serviceRequest={undefined}
            from={undefined}
            onClose={() => setIsServiceRequestFormOpen(false)}
          />
        )}
        <FilterBar
          filters={filters}
          applyFilter={applyFilter}
          removeFilter={removeFilter}
        />
        <ServiceRequestDashboardTable
          serviceRequests={visibleServiceRequests}
          selectedServiceRequests={selectedIds}
          onSelectServiceRequest={(id: string) =>
            handleSelectServiceRequests([id])
          }
          onSelectGroup={handleSelectServiceRequests}
          onDeleteGroup={handleDeleteGroup}
          onRemoveFromGroup={handleDeleteItemFromGroup}
          onSelectAll={toggleAllSelected}
          allSelected={allSelected}
          loading={loading}
          showColumnSelectionDialog={showColumnSelectionDialog}
          onCloseColumnSelectionDialog={closeColumnSelectionDialog}
          persistKey={PAGINATION_KEYS.serviceRequestDashboard}
        />
      </div>
    </WithNavigation>
  );
};
